import { useEffect, useState } from 'react';
import {
  getMoviesApiUrl,
  getUnavailableApiMessage,
  parseJsonResponse,
} from '../../../lib/moviesAdminApi';

export const useMoviesAdminSession = ({ enabled = true } = {}) => {
  const [sessionStatus, setSessionStatus] = useState('checking');
  const [adminSecret, setAdminSecret] = useState('');
  const [loginMessage, setLoginMessage] = useState('');

  useEffect(() => {
    if (!enabled) {
      setSessionStatus('api-unavailable');
      setLoginMessage(
        'Painel administrativo indisponivel neste deploy. Use o dominio principal no Vercel.'
      );
      return undefined;
    }

    let isMounted = true;

    const loadSession = async () => {
      try {
        const response = await fetch(getMoviesApiUrl('/api/movies/session'), {
          method: 'GET',
          credentials: 'include',
        });
        const data = await parseJsonResponse(response);

        if (!isMounted) {
          return;
        }

        if (response.status === 404) {
          setSessionStatus('api-unavailable');
          setLoginMessage(getUnavailableApiMessage());
          return;
        }

        setSessionStatus(data?.authenticated ? 'authenticated' : 'anonymous');
      } catch (_error) {
        if (isMounted) {
          setSessionStatus('api-unavailable');
          setLoginMessage(getUnavailableApiMessage());
        }
      }
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!enabled) {
      return;
    }

    if (!adminSecret.trim()) {
      setLoginMessage('Digite a credencial administrativa antes de liberar a edicao.');
      return;
    }

    setSessionStatus('authenticating');
    setLoginMessage('');

    try {
      const response = await fetch(getMoviesApiUrl('/api/movies/session'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ secret: adminSecret.trim() }),
      });
      const data = await parseJsonResponse(response);

      if (response.status === 404) {
        setSessionStatus('api-unavailable');
        setLoginMessage(getUnavailableApiMessage());
        return;
      }

      if (!response.ok) {
        setSessionStatus('anonymous');
        setLoginMessage(data?.error || 'Nao foi possivel autenticar a sessao administrativa.');
        return;
      }

      setAdminSecret('');
      setSessionStatus('authenticated');
      setLoginMessage('Sessao administrativa liberada. Agora voce pode enviar novas entradas.');
    } catch (_error) {
      setSessionStatus('api-unavailable');
      setLoginMessage(getUnavailableApiMessage());
    }
  };

  const handleLogout = async () => {
    if (!enabled) {
      return;
    }

    try {
      await fetch(getMoviesApiUrl('/api/movies/session'), {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } finally {
      setSessionStatus('anonymous');
      setLoginMessage('Sessao encerrada neste navegador.');
    }
  };

  return {
    sessionStatus,
    adminSecret,
    setAdminSecret,
    loginMessage,
    handleLogin,
    handleLogout,
  };
};
